/**
 * Wavlynq sibling-app registry — v3.1
 *
 * Wavlynq is the umbrella for 9 sibling apps with identical full feature
 * sets. v1 ships Fryqnc, Pulsynq, MindLynq. The other 6 are in active
 * development and pre-staged here so their crisis resources, brand
 * metadata, and routing are ready when they ship.
 *
 * NOTE: The runtime `AppId` type (src/lib/apps.ts) intentionally only
 * lists the v1 trio because the rest of the app's typing is built around
 * those three. This file is the broader source of truth used by the
 * crisis resources tab, the master spec, and the founder dashboard.
 */

export type SiblingAppId =
  | "fryqnc"
  | "pulsynq"
  | "mindlynq"
  | "spiritsynq"
  | "jadesynq"
  | "deafwav"
  | "ablelynq"
  | "fullynq"
  | "openlynq";

export type LaunchPhase = "v1" | "v2";

export interface CrisisLine {
  name: string;
  phone?: string;
  sms?: string;
  url?: string;
  notes?: string;
}

export interface SiblingApp {
  id: SiblingAppId;
  name: string;
  community: string;
  subdomain: string;
  phase: LaunchPhase;
  /** Per-community crisis resources, surfaced by the floating Help tab. */
  crisis: CrisisLine[];
}

export const SIBLING_APPS: SiblingApp[] = [
  {
    id: "fryqnc",
    name: "Fryqnc",
    community: "LGBTQIA+ / Trans & Non-Binary",
    subdomain: "fryqnc.com",
    phase: "v1",
    crisis: [
      {
        name: "Trans Lifeline",
        phone: "1-877-565-8860",
        notes: "Peer support, by us, for us",
      },
      {
        name: "The Trevor Project",
        phone: "1-866-488-7386",
        sms: "678-678",
        url: "https://www.thetrevorproject.org/",
        notes: "LGBTQ youth crisis support, 24/7",
      },
    ],
  },
  {
    id: "pulsynq",
    name: "Pulsynq",
    community: "Black / African American",
    subdomain: "pulsynq.com",
    phase: "v1",
    crisis: [
      {
        name: "BEAM — Black Emotional and Mental Health Collective",
        url: "https://beam.community/",
        notes: "Healing, wellness, and liberation for Black communities",
      },
      {
        name: "The Loveland Foundation Therapy Fund",
        url: "https://thelovelandfoundation.org/",
        notes: "Free therapy access for Black women and girls",
      },
    ],
  },
  {
    id: "mindlynq",
    name: "MindLynq",
    community: "Neurodivergent (Autism, ADHD, sensory-processing)",
    subdomain: "mindlynq.com",
    phase: "v1",
    crisis: [
      {
        name: "Autism Society Helpline",
        phone: "1-800-328-8476",
        url: "https://autismsociety.org/",
        notes: "Information and support",
      },
      {
        name: "Autistic Self Advocacy Network",
        url: "https://autisticadvocacy.org/",
        notes: "Nothing about us, without us",
      },
    ],
  },
  {
    id: "spiritsynq",
    name: "Spiritsynq",
    community: "Indigenous / Native American",
    subdomain: "spiritsynq.com",
    phase: "v2",
    crisis: [
      {
        name: "StrongHearts Native Helpline",
        phone: "1-844-762-8483",
        url: "https://strongheartshelpline.org/",
        notes: "Culturally appropriate support, 24/7",
      },
    ],
  },
  {
    id: "jadesynq",
    name: "Jadesynq",
    community: "Pan-Asian",
    subdomain: "jadesynq.com",
    phase: "v2",
    crisis: [
      {
        name: "Asian Mental Health Collective",
        url: "https://www.asianmhc.org/",
      },
      {
        name: "NAAPIMHA — National Asian American Pacific Islander Mental Health Assoc.",
        url: "https://www.naapimha.org/",
      },
    ],
  },
  {
    id: "deafwav",
    name: "Deafwav",
    community: "Deaf / Hard of Hearing",
    subdomain: "deafwav.com",
    phase: "v2",
    crisis: [
      {
        name: "DeafLEAD Crisis Hotline",
        phone: "321-800-3323",
        url: "https://www.deaflead.com/",
        notes: "Video relay crisis support",
      },
    ],
  },
  {
    id: "ablelynq",
    name: "Ablelynq",
    community: "Chronically Ill / Disabled",
    subdomain: "ablelynq.com",
    phase: "v2",
    crisis: [
      {
        name: "Disability Rights & Resources Crisis Line",
        url: "https://www.disabilityrights.org/",
      },
    ],
  },
  {
    id: "fullynq",
    name: "Fullynq",
    community: "Plus Size / Body Positive",
    subdomain: "fullynq.com",
    phase: "v2",
    crisis: [
      {
        name: "Alliance for Eating Disorders Awareness Helpline",
        phone: "1-866-662-1235",
        url: "https://www.allianceforeatingdisorders.com/",
        notes: "Free, confidential help finding eating-disorder treatment",
      },
    ],
  },
  {
    id: "openlynq",
    name: "Openlynq",
    community: "ENM / Polyamorous",
    subdomain: "openlynq.com",
    phase: "v2",
    crisis: [
      {
        name: "The Trevor Project",
        phone: "1-866-488-7386",
        url: "https://www.thetrevorproject.org/",
      },
    ],
  },
];

export const GLOBAL_CRISIS_LINES: CrisisLine[] = [
  {
    name: "988 — Suicide & Crisis Lifeline",
    phone: "988",
    notes: "Call or text, US, 24/7",
  },
  {
    name: "Crisis Text Line",
    sms: "741741",
    notes: "Text HOME to 741741",
  },
];

export const getSiblingApp = (id: SiblingAppId | string | null | undefined): SiblingApp | null =>
  SIBLING_APPS.find((a) => a.id === id) ?? null;

/**
 * Detect the current sibling app from window.location.hostname when the
 * app is hosted on its own subdomain (fryqnc.com, pulsynq.com, etc.).
 * Returns null in dev / preview hosts where the URL doesn't carry the
 * sibling identity.
 */
export const detectSiblingFromHost = (): SiblingAppId | null => {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  for (const app of SIBLING_APPS) {
    if (host === app.subdomain || host.endsWith(`.${app.subdomain}`)) {
      return app.id;
    }
  }
  return null;
};
